/**
 * Auth helpers for quick-quote — JWT in localStorage, paneliq /auth/* API.
 */

const TOKEN_KEY = 'paneliq_quick_quote_token';

export function apiBaseUrl() {
  // Vite bakes import.meta.env.VITE_API_BASE_URL into the bundle at build time.
  // Falls back to localhost:8080 for dev runs (vite dev server on :3000).
  return (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080').replace(/\/$/, '');
}

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; }
  catch { return null; }
}

export function setToken(token) {
  try { localStorage.setItem(TOKEN_KEY, token); }
  catch { /* private mode etc. — caller can decide to surface */ }
}

export function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); }
  catch { /* swallow */ }
}

export function isAuthed() {
  return !!getToken();
}

/**
 * Authenticated fetch. Injects Authorization: Bearer <jwt>.
 * On 401, clears the token (caller is expected to re-render and show the login page).
 */
export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : apiBaseUrl() + path;
  const token = getToken();
  const headers = new Headers(options.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    clearToken();
  }
  return response;
}

/**
 * Convenience: POST /auth/login. Returns the parsed body on success, throws on failure.
 */
export async function loginRequest(email, password) {
  const response = await fetch(apiBaseUrl() + '/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  let body = {};
  try { body = await response.json(); } catch { /* keep {} */ }
  if (!response.ok) {
    throw new Error(body.detail || `Login failed (${response.status})`);
  }
  return body; // { access_token, token_type, user, organization }
}

/**
 * Verify the current token by calling /auth/me. Returns the user on success,
 * null on failure (and clears the stored token).
 */
export async function fetchMe() {
  const token = getToken();
  if (!token) return null;
  try {
    const response = await apiFetch('/auth/me');
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}
